"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { formatCurrency } from "@/lib/utils/pricing"
import type { ActiveCampaign } from "@/lib/donations/getActiveCampaign"

interface Props {
  campaign: ActiveCampaign
  /** Total de la orden (subtotal + tax + fee) antes de donación, para el redondeo. */
  orderTotal: number
  /** Slug del local, para enlazar su política de devoluciones. */
  slug: string
  accentColor: string
  /** monto a-la-causa elegido (0 = no donar). */
  onContinue: (donationAmount: number) => void
  onBack: () => void
}

export function DonationStep({ campaign, orderTotal, slug, accentColor, onContinue, onBack }: Props) {
  const t = useTranslations("public.microsite.donation")
  const [selected, setSelected] = useState<number | null>(null)
  const [custom, setCustom] = useState("")
  const roundUp = Math.round((Math.ceil(orderTotal) - orderTotal) * 100) / 100
  const chosen = selected ?? (parseFloat(custom) || 0)

  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="text-center">
        <div className="text-4xl">🇻🇪</div>
        <h2 className="text-xl font-black mt-1" style={{ color: "#1A1A1A" }}>{campaign.title}</h2>
        <p className="text-sm text-gray-500 mt-1">{t("subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {campaign.impactTiers.map((tier) => (
          <button
            key={tier.amount}
            onClick={() => { setSelected(tier.amount); setCustom("") }}
            className="rounded-xl border px-3 py-3 text-left transition-all"
            style={{ borderColor: selected === tier.amount ? accentColor : "#e5e7eb", borderWidth: selected === tier.amount ? 2 : 1 }}
          >
            <span className="block text-lg font-black">{formatCurrency(tier.amount)}</span>
            <span className="block text-[11px] text-gray-500">{tier.label}</span>
          </button>
        ))}
      </div>

      {roundUp > 0 && (
        <button
          onClick={() => { setSelected(roundUp); setCustom("") }}
          className="rounded-xl border px-3 py-2 text-sm font-semibold"
          style={{ borderColor: selected === roundUp ? accentColor : "#e5e7eb" }}
        >
          {t("roundUp", { amount: formatCurrency(roundUp) })}
        </button>
      )}

      <input
        inputMode="decimal" placeholder={t("customPlaceholder")}
        value={custom} onChange={(e) => { setCustom(e.target.value); setSelected(null) }}
        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-base"
      />

      {/* Divulgación en el punto de decisión. Antes, la aceptación de la
          Política de Reembolso vivía en un checkbox de CustomerStep, dos
          pantallas después de elegir el monto — y nunca decía que la donación
          no se devuelve. */}
      <p className="text-[11px] leading-relaxed text-gray-500">
        {t.rich("nonRefundable", {
          // El resto del pedido lo rige la política DEL RESTAURANTE; la de
          // plataforma se conserva porque es quien procesa el cobro.
          merchantRefund: (chunks) => (
            <a href={`/order/${slug}/refund-policy`} target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accentColor }}>{chunks}</a>
          ),
          refund: (chunks) => (
            <a href="/refund" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: accentColor }}>{chunks}</a>
          ),
        })}
      </p>

      <button
        onClick={() => onContinue(chosen > 0 ? chosen : 0)}
        className="w-full rounded-xl py-3 font-bold text-white"
        style={{ background: accentColor }}
      >
        {chosen > 0 ? t("continueWithAmount", { amount: formatCurrency(chosen) }) : t("continue")}
      </button>
      <button onClick={() => onContinue(0)} className="text-sm text-gray-500">{t("noThanks")}</button>
      <button onClick={onBack} className="text-sm text-gray-400">{t("back")}</button>
    </div>
  )
}
